/**
 * Flight.tsx
 * Log a flight: Date, TachStart, TachEnd, Notes, optional co-partners.
 * TachStart is prefilled from GET /config (currentTach).
 * Hours = TachEnd - TachStart, shown read-only.
 *
 * Shared flight: when one or more co-partners are checked, a live split
 * preview is shown and hours are divided N ways (last partner absorbs
 * any rounding remainder to keep the sum exact).
 */

import React, { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../auth'
import { getConfig, submitFlight, readTachPhoto, ApiError } from '../api'
import PhotoCapture from '../components/PhotoCapture'
import type { Partner } from '../types'

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

type PageState = 'form' | 'confirming' | 'submitting' | 'done'

/** Mirror of the backend splitHours() — round to 1 decimal, last absorbs remainder. */
function splitHours(totalHours: number, n: number): number[] {
  const roundTenth = (x: number) => Math.round(x * 10) / 10
  if (n <= 1) return [roundTenth(totalHours)]
  const perPartner = roundTenth(totalHours / n)
  const allocations: number[] = Array(n).fill(perPartner)
  const remainder = roundTenth(totalHours - roundTenth(perPartner * n))
  if (remainder !== 0) {
    allocations[n - 1] = roundTenth(allocations[n - 1] + remainder)
  }
  return allocations
}

export default function Flight() {
  const { user } = useAuth()
  const navigate = useNavigate()

  const [date, setDate] = useState(today())
  const [tachStart, setTachStart] = useState('')
  const [tachEnd, setTachEnd] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState('')
  const [successMsg, setSuccessMsg] = useState('')
  const [pageState, setPageState] = useState<PageState>('form')

  // Partners available as co-pilots (all active partners except the caller).
  const [otherPartners, setOtherPartners] = useState<Partner[]>([])
  const [callerPartner, setCallerPartner] = useState<Partner | null>(null)
  // Set of selected co-partner IDs.
  const [selectedCoIds, setSelectedCoIds] = useState<Set<string>>(new Set())

  // currentTach is fetched from /config — used to prefill TachStart AND to
  // sanity-check tach-photo OCR results (a panel photo should always show a
  // value >= the known current tach; lower means the model misread it).
  const [currentTach, setCurrentTach] = useState<number | null>(null)

  // ---- Tach photo OCR state ------------------------------------------------
  // The user can snap a photo of the engine instrument panel; the lambda runs
  // it through Bedrock vision and pulls the "TACH: <hours>" reading. We then
  // let the partner pick whether to use it as Start or End.
  type TachOcrState = 'idle' | 'reading' | 'done' | 'error'
  const [tachOcrState, setTachOcrState] = useState<TachOcrState>('idle')
  const [tachOcrValue, setTachOcrValue] = useState<number | null>(null)
  const [tachOcrMsg, setTachOcrMsg] = useState<string>('')

  // Prefill TachStart from config; also load partner list for co-pilot checkboxes.
  useEffect(() => {
    getConfig()
      .then((cfg) => {
        if (cfg.currentTach != null) {
          setTachStart(String(cfg.currentTach))
          setCurrentTach(cfg.currentTach)
        }
        if (cfg.caller) {
          setCallerPartner(cfg.caller)
          // All partners except the caller are potential co-pilots.
          setOtherPartners(
            (cfg.partners ?? []).filter((p) => p.id !== cfg.caller.id)
          )
        }
      })
      .catch(() => {
        // Config load failure is non-fatal — user can enter manually.
      })
  }, [])

  /**
   * After PhotoCapture uploads the panel photo, run the tach OCR. The model
   * returns { tach: number, confidence: 'high'|'medium'|'low' }. We surface
   * any 'low' confidence as a warning and refuse to apply the value if it's
   * less than the known currentTach (tach hours only ever advance).
   */
  async function handleTachPhotoUploaded(key: string): Promise<void> {
    setTachOcrState('reading')
    setTachOcrMsg('Reading tach value from photo…')
    setTachOcrValue(null)
    try {
      const result = await readTachPhoto(key)
      if (result.tach == null || result.confidence === 'low') {
        setTachOcrState('error')
        setTachOcrMsg(
          result.reason
            ? `Could not read tach (${result.reason}). Enter manually.`
            : 'Could not read tach from the photo. Enter manually.',
        )
        return
      }
      if (currentTach != null && result.tach < currentTach) {
        // OCR returned a value lower than the known tach — almost certainly a
        // misread digit. Refuse to apply rather than silently regressing the
        // logbook.
        setTachOcrState('error')
        setTachOcrMsg(
          `Extracted value ${result.tach.toFixed(1)} is below the current tach ` +
          `(${currentTach.toFixed(1)}). The photo may be blurry — try again or enter manually.`,
        )
        return
      }
      setTachOcrValue(result.tach)
      setTachOcrState('done')
      setTachOcrMsg(
        result.confidence === 'high'
          ? `Read ${result.tach.toFixed(1)} hrs from photo.`
          : `Read ${result.tach.toFixed(1)} hrs (medium confidence — please verify).`,
      )
    } catch (err) {
      setTachOcrState('error')
      setTachOcrMsg(err instanceof Error ? err.message : 'Tach read failed.')
    }
  }

  function applyOcrTo(target: 'start' | 'end'): void {
    if (tachOcrValue == null) return
    const formatted = tachOcrValue.toFixed(1)
    if (target === 'start') setTachStart(formatted)
    else setTachEnd(formatted)
    // Clear the OCR widget so the partner sees the value land in the field.
    setTachOcrState('idle')
    setTachOcrValue(null)
    setTachOcrMsg('')
  }

  const start = parseFloat(tachStart)
  const end = parseFloat(tachEnd)
  const totalHours =
    !isNaN(start) && !isNaN(end) && end >= start
      ? Math.round((end - start) * 10) / 10
      : null

  // All billed partners: caller first, then selected co-partners in the order they appear.
  const billedPartners: Partner[] = [
    ...(callerPartner ? [callerPartner] : []),
    ...otherPartners.filter((p) => selectedCoIds.has(p.id)),
  ]
  const allocations =
    totalHours !== null ? splitHours(totalHours, billedPartners.length) : []

  const showSplitPreview = selectedCoIds.size > 0 && totalHours !== null

  function toggleCoPartner(id: string) {
    setSelectedCoIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  function handleReview(e: React.FormEvent) {
    e.preventDefault()
    setError('')

    if (!date) return setError('Date is required.')
    if (tachStart === '') return setError('Tach Start is required.')
    if (tachEnd === '') return setError('Tach End is required.')
    if (isNaN(start) || isNaN(end)) return setError('Tach values must be numbers.')
    if (end < start) return setError('Tach End must be ≥ Tach Start.')
    if (totalHours === null || totalHours <= 0) {
      return setError('Flight must have at least 0.1 hours.')
    }

    setPageState('confirming')
  }

  async function handleConfirmSubmit() {
    setError('')
    setPageState('submitting')
    try {
      const coPartners = selectedCoIds.size > 0 ? [...selectedCoIds] : undefined
      const result = await submitFlight({
        date,
        tachStart: start,
        tachEnd: end,
        notes,
        ...(coPartners ? { coPartners } : {}),
      })
      if (!result.ok) {
        setError(result.message || 'Could not save.')
        setPageState('form')
        return
      }
      setSuccessMsg(result.message)
      setPageState('done')
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        navigate('/not-authorized')
        return
      }
      setError(err instanceof Error ? err.message : 'Unexpected error.')
      setPageState('form')
    }
  }

  function handleLogAnother() {
    setDate(today())
    setTachStart(tachEnd) // natural: next flight starts where last ended
    setTachEnd('')
    setNotes('')
    setError('')
    setSuccessMsg('')
    setSelectedCoIds(new Set())
    setPageState('form')
  }

  return (
    <div className="wrap">
      <div className="brand">
        <div className="brand-logo">N73AA</div>
        <div className="brand-tag">
          <Link to="/" className="back-link">&#8592; Back</Link>
          &ensp;Beechcraft Bonanza N35 &mdash; Log a Flight
        </div>
      </div>

      {user && (
        <div className="who">
          Logging as: <strong>{user.name}</strong>
        </div>
      )}

      {pageState !== 'done' ? (
        <form className="card" onSubmit={handleReview} noValidate>
          {error && <div className="msg msg-err">{error}</div>}

          <label htmlFor="date">Date</label>
          <input
            id="date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
            autoFocus
          />

          {/* Tach photo OCR — snap a panel photo and the lambda reads the
              digital "TACH: <hours>" value. The extracted value is shown with
              two buttons so the partner picks Start or End rather than us
              guessing. Falls back to manual entry on any error. */}
          {callerPartner && (
            <div className="tach-ocr-block">
              <PhotoCapture
                label="tach"
                date={date}
                partnerId={callerPartner.id}
                buttonLabel="📷 Read tach from panel photo"
                onUploadComplete={handleTachPhotoUploaded}
                onUploadError={(msg) => {
                  setTachOcrState('error')
                  setTachOcrMsg(msg)
                }}
              />
              {tachOcrMsg && (
                <p
                  className="field-note"
                  style={{
                    color: tachOcrState === 'error' ? '#c5221f' : '#137333',
                  }}
                >
                  {tachOcrMsg}
                </p>
              )}
              {tachOcrState === 'done' && tachOcrValue != null && (
                <div className="tach-ocr-apply">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => applyOcrTo('start')}
                  >
                    Use {tachOcrValue.toFixed(1)} as Tach Start
                  </button>
                  <button
                    type="button"
                    className="btn btn-flight"
                    onClick={() => applyOcrTo('end')}
                  >
                    Use {tachOcrValue.toFixed(1)} as Tach End
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="row2">
            <div>
              <label htmlFor="tachStart">Tach Start</label>
              <input
                id="tachStart"
                type="number"
                step="0.1"
                inputMode="decimal"
                value={tachStart}
                onChange={(e) => setTachStart(e.target.value)}
                placeholder="e.g. 1234.5"
                required
              />
            </div>
            <div>
              <label htmlFor="tachEnd">Tach End</label>
              <input
                id="tachEnd"
                type="number"
                step="0.1"
                inputMode="decimal"
                value={tachEnd}
                onChange={(e) => setTachEnd(e.target.value)}
                placeholder="e.g. 1235.2"
                required
              />
            </div>
          </div>

          <label>Hours (computed)</label>
          <div className="computed-display">
            {totalHours !== null ? `${totalHours.toFixed(1)} hrs` : '—'}
          </div>

          <label htmlFor="notes">Notes</label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Destination, passengers, remarks…"
          />

          {/* Co-partner section — only shown when there are other partners. */}
          {otherPartners.length > 0 && (
            <div className="co-partner-section">
              <label className="co-partner-heading">
                Other partners on this flight (optional)
              </label>
              <div className="co-partner-checks">
                {otherPartners.map((p) => (
                  <label key={p.id} className="co-partner-check-label">
                    <input
                      type="checkbox"
                      className="co-partner-checkbox"
                      checked={selectedCoIds.has(p.id)}
                      onChange={() => toggleCoPartner(p.id)}
                    />
                    {p.name}
                  </label>
                ))}
              </div>

              {showSplitPreview && (
                <div className="split-preview">
                  Hours split {billedPartners.length} ways:&nbsp;
                  {billedPartners.map((p, i) => (
                    <span key={p.id} className="split-preview-item">
                      {p.name} {allocations[i].toFixed(1)}h
                      {i < billedPartners.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          <button
            className="btn btn-flight"
            type="submit"
            disabled={pageState === 'submitting' || pageState === 'confirming'}
          >
            Review &amp; Submit
          </button>
          <button
            className="btn btn-secondary"
            type="button"
            onClick={() => navigate('/')}
          >
            Cancel
          </button>
        </form>
      ) : (
        <div className="card">
          <div className="msg msg-ok">{successMsg}</div>
          <div className="confirm-actions">
            <button className="btn btn-flight" onClick={handleLogAnother}>
              Log another flight
            </button>
            <button className="btn btn-neutral" onClick={() => navigate('/')}>
              Back to menu
            </button>
          </div>
        </div>
      )}

      <div className="page-foot">N73AA partnership log.</div>

      {(pageState === 'confirming' || pageState === 'submitting') && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal-card">
            <h2 className="modal-title">Confirm flight</h2>
            <div className="modal-summary">
              <div className="modal-summary-row">
                <span className="modal-summary-label">Date</span>
                <span className="modal-summary-value">{date}</span>
              </div>
              <div className="modal-summary-row">
                <span className="modal-summary-label">Tach</span>
                <span className="modal-summary-value">
                  {tachStart} &rarr; {tachEnd}
                </span>
              </div>
              <div className="modal-summary-row">
                <span className="modal-summary-label">Hours</span>
                <span className="modal-summary-value">
                  {totalHours !== null ? `${totalHours.toFixed(1)} hrs` : '—'}
                </span>
              </div>
              {billedPartners.length > 1 && (
                <div className="modal-summary-row">
                  <span className="modal-summary-label">Split</span>
                  <span className="modal-summary-value">
                    {billedPartners
                      .map((p, i) => `${p.name} ${allocations[i].toFixed(1)}h`)
                      .join(', ')}
                  </span>
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-flight"
                type="button"
                onClick={handleConfirmSubmit}
                disabled={pageState === 'submitting'}
              >
                {pageState === 'submitting' ? 'Saving…' : 'Confirm & submit'}
              </button>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => setPageState('form')}
                disabled={pageState === 'submitting'}
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
